/**
 * Per-tab safety copy for text that has not been acknowledged by Patchbay yet (#1564).
 * Keys include the signed-in person, case and pane so a shared browser never restores one
 * person's draft for another. This is a fallback, not the source of truth: successful reply
 * autosaves reconcile the case cache and remove their matching safety copy.
 */
import type { PatchbayDraftSaveIn } from '../case-view/api'

const PREFIX = 'patchbay:composer:v1:'

export interface DraftSessionRef {
  viewerId: string | null | undefined
  caseId: string
  paneId: string
}

export interface ReplyDraftSnapshot {
  kind: 'reply'
  marker: string
  payload: PatchbayDraftSaveIn
  updatedAt: number
}

export interface InternalDraftMention {
  zuid: string
  name: string
  token: string
}

export interface InternalDraftSnapshot {
  kind: 'internal'
  text: string
  author: string
  picked: InternalDraftMention[]
  updatedAt: number
}

function sessionStore(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}

function storageKey(kind: ReplyDraftSnapshot['kind'] | InternalDraftSnapshot['kind'], ref: DraftSessionRef): string | null {
  if (!ref.viewerId || !ref.caseId || !ref.paneId) return null
  return `${PREFIX}${kind}:${ref.viewerId}:${ref.caseId}:${ref.paneId}`
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

export function readReplyDraft(ref: DraftSessionRef): ReplyDraftSnapshot | null {
  const key = storageKey('reply', ref)
  if (!key) return null
  try {
    const raw = sessionStore()?.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ReplyDraftSnapshot>
    const payload = parsed.payload as Partial<PatchbayDraftSaveIn> | undefined
    if (
      parsed.kind !== 'reply' ||
      typeof parsed.marker !== 'string' ||
      typeof parsed.updatedAt !== 'number' ||
      typeof payload?.thread_id !== 'string' ||
      typeof payload.body_html !== 'string' ||
      !isStringArray(payload.to) ||
      !isStringArray(payload.cc)
    ) {
      return null
    }
    return parsed as ReplyDraftSnapshot
  } catch {
    return null
  }
}

/** Returns false when session storage is unavailable; autosave still continues without the fallback. */
export function writeReplyDraft(ref: DraftSessionRef, snapshot: ReplyDraftSnapshot): boolean {
  const key = storageKey('reply', ref)
  if (!key) return false
  try {
    sessionStore()?.setItem(key, JSON.stringify(snapshot))
    return sessionStore()?.getItem(key) !== null
  } catch {
    return false
  }
}

/** Remove only the request the server acknowledged; a newer edit must stay recoverable. */
export function clearReplyDraft(ref: DraftSessionRef, marker?: string): void {
  const key = storageKey('reply', ref)
  if (!key) return
  try {
    if (marker && readReplyDraft(ref)?.marker !== marker) return
    sessionStore()?.removeItem(key)
  } catch {
    /* convenience only */
  }
}

export function readInternalDraft(ref: DraftSessionRef): InternalDraftSnapshot | null {
  const key = storageKey('internal', ref)
  if (!key) return null
  try {
    const raw = sessionStore()?.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<InternalDraftSnapshot>
    if (
      parsed.kind !== 'internal' ||
      typeof parsed.text !== 'string' ||
      typeof parsed.author !== 'string' ||
      typeof parsed.updatedAt !== 'number' ||
      !Array.isArray(parsed.picked) ||
      !parsed.picked.every(
        (item) =>
          item &&
          typeof item === 'object' &&
          typeof item.zuid === 'string' &&
          typeof item.name === 'string' &&
          typeof item.token === 'string',
      )
    ) {
      return null
    }
    return parsed as InternalDraftSnapshot
  } catch {
    return null
  }
}

export function writeInternalDraft(ref: DraftSessionRef, snapshot: InternalDraftSnapshot | null): void {
  const key = storageKey('internal', ref)
  if (!key) return
  try {
    if (snapshot?.text.trim()) sessionStore()?.setItem(key, JSON.stringify(snapshot))
    else sessionStore()?.removeItem(key)
  } catch {
    /* convenience only */
  }
}

/** Sign-out/401 cleanup: composer text is staff data and must not remain for the next browser user. */
export function clearComposerSessionDrafts(): void {
  try {
    const store = sessionStore()
    if (!store) return
    for (let index = store.length - 1; index >= 0; index -= 1) {
      const key = store.key(index)
      if (key?.startsWith(PREFIX)) store.removeItem(key)
    }
  } catch {
    /* cleanup is best effort when browser storage is disabled */
  }
}
